import { computed, inject } from '@angular/core';
import { Task } from 'projects/shared/src/lib/core/interfaces/task';
import { TaskItemResponse } from 'projects/shared/src/lib/core/interfaces/task-item-response';
import { TaskItemResponseElement } from 'projects/shared/src/lib/core/interfaces/task-item-response-element';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { TaskItem } from 'projects/shared/src/lib/core/interfaces/task-item';
import { normalizeTask } from '../core/helpers/exercise-store.helpers';
import { LoadTaskFromItemParams, LoadTaskParams } from '../core/types/exercise-flow.types';
import { EXERCISE_FLOW_ADAPTER_TOKEN } from '../core/injection-tokens/exercise-flow-adapter.token';

type ExerciseState = {
  activeTask: Task | null;
  exampleTaskItem: TaskItem | null;
  taskItemIndex: number;
  answersDone: TaskItemResponse[];
  isLoading: boolean;
  isExampleTaskActive: boolean;
  isForcedDone: boolean;
  wrongAttempts: number;
};

const initialState: ExerciseState = {
  activeTask: null,
  exampleTaskItem: null,
  taskItemIndex: -1,
  answersDone: [],
  isLoading: false,
  isExampleTaskActive: false,
  isForcedDone: false,
  wrongAttempts: 0,
};

export const ExerciseStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ activeTask, taskItemIndex }) => ({
    itemsAmount: computed(() => {
      const currentTask = activeTask();
      if (currentTask) {
        return currentTask.items.length;
      }
      return 0;
    }),
    currentItem: computed(() => {
      const currentTask = activeTask();
      if (currentTask) {
        return currentTask.items[taskItemIndex()];
      }
      return null;
    }),
  })),
  withMethods((store, flow = inject(EXERCISE_FLOW_ADAPTER_TOKEN)) => ({
    async loadTask(params: LoadTaskParams) {
      try {
        patchState(store, { isLoading: true });

        const task = await flow.loadTask(params);
        const normalized = normalizeTask(task);

        patchState(store, {
          ...normalized,
          isForcedDone: false,
          isLoading: false,
        });
      } catch {
        patchState(store, { isLoading: false });
      }
    },
    async loadTaskFromItem(params: LoadTaskFromItemParams) {
      try {
        patchState(store, { isLoading: true });

        const taskItem = await flow.loadTaskFromItem?.(params);
        if (!taskItem) {
          throw new Error('loadTaskFromItem is not implemented for this flow');
        }

        patchState(store, {
          activeTask: {
            id: 0,
            taskResultId: 0,
            title: '',
            name: '',
            description: '',
            maxDuration: 180,
            answersTotalDuration: 0,
            mustStartItemId: taskItem.id,
            mustStartItemAttempted: false,
            items: [taskItem],
          },
          isForcedDone: false,
          isLoading: false,
          taskItemIndex: 0,
          exampleTaskItem: null,
          isExampleTaskActive: false,
        });
      } catch {
        patchState(store, { isLoading: false });
      }
    },
    async processAnswer(
      answers: TaskItemResponseElement[],
      isAttempt: boolean,
      duration?: number
    ) {
      const activeTask = store.activeTask();
      if (!activeTask) return;

      const lastActiveItemDuration = activeTask.lastActiveItemDuration ?? 0;
      if (activeTask) {
        patchState(store, {
          activeTask: {
            ...activeTask,
            answersTotalDuration: activeTask.answersTotalDuration + lastActiveItemDuration,
            lastActiveItemDuration: undefined,

          }
        });
        duration = duration ? duration + lastActiveItemDuration : duration;
      }

      const items = activeTask.items;
      const currentItemIndex = store.taskItemIndex();
      const currentItem = items[currentItemIndex];
      if (!currentItem) return;

      answers.forEach((answer) => {
        answer.duration = duration ?? 0;
      });

      try {
        patchState(store, { isLoading: true });

        await flow.processAnswer({
          answers,
          taskResultId: activeTask.taskResultId,
          taskItemId: currentItem.id,
          isAttempt,
          duration,
          resetLastActiveItemDuration: lastActiveItemDuration > 0,
          context: {
            isLastItem: currentItemIndex === items.length - 1,
            isForcedDone: store.isForcedDone(),
            itemsAmount: items.length,
            taskItemIndex: currentItemIndex,
          },
        });

        if (!isAttempt) {
          patchState(store, (state) => ({
            answersDone: [
              ...state.answersDone,
              {
                taskItemId: currentItem.id,
                elements: answers,
              },
            ],
          }));
        }
      } finally {
        patchState(store, { isLoading: false });
      }
    },
    setExampleTaskVisiblity(state: boolean) {
      try {
        patchState(store, { isLoading: true });
        patchState(store, { isExampleTaskActive: state, isLoading: false });
      } catch {
        patchState(store, { isLoading: false });
      }
    },
    getCurrentTask() {
      const currentTask = store.activeTask();
      if (currentTask) return currentTask;

      return null;
    },
    getCurrentItem() {
      const currentTask = store.activeTask();
      if (currentTask) {
        const items = store.activeTask()?.items ?? [];
        return items[store.taskItemIndex()] ?? null;
      }

      return null;
    },
    getCurrentItemAnswer() {
      const currentItem = this.getCurrentItem();
      if (currentItem) {
        return store.answersDone().find((answer) => answer.taskItemId === currentItem.id) ?? null;
      }

      return null;
    },
    getRemainingItems() {
      let items = store.activeTask()?.items ?? [];

      return items.filter(
        (item) => !store.answersDone().some((answer) => answer.taskItemId === item.id)
      );
    },
    isFinishReady() {
      if (store.isForcedDone()) {
        return true;
      }

      const currentTask = store.activeTask();
      if (currentTask) {
        return store.taskItemIndex() == currentTask.items.length - 1;
      }
      return false;
    },
    moveBack() {
      const currentItemIndex = store.taskItemIndex();
      if (store.activeTask() && currentItemIndex > 0) {
        if (currentItemIndex > 0) {
          patchState(store, { taskItemIndex: currentItemIndex - 1 });
        }
      }
    },
    getWrongAttempts() {
      return store.wrongAttempts();
    },
    setWrongAttempts() {
      const wrongAttempts = store.wrongAttempts();
      patchState(store, { wrongAttempts: wrongAttempts + 1 });
    },
    moveForward() {
      const currentTask = store.activeTask();
      if (currentTask) {
        const items = store.activeTask()?.items ?? [];
        const currentItemIndex = store.taskItemIndex();
        if (currentItemIndex < items.length - 1) {
          patchState(store, { taskItemIndex: currentItemIndex + 1 });
        }
      }
    },
    setIsForcedDone(state: boolean) {
      patchState(store, { isForcedDone: state });
    },
    setTaskResultId(taskResultId: number) {
      const activeTask = store.activeTask();
      if (!activeTask) return;

      patchState(store, { activeTask: { ...activeTask, taskResultId } });
    },
    async forceEndTask() {
      // TODO, refactor, find a better way to handle this !!

      // if this was the forced last answers, send remaining empty answers to BE
      // so ... answer is generated by (item-base.component.ts/task-item-template-base.component.ts)
      // to get that object, the templateFactory is needed
      // to not generate a component for each empty answers that is still needed, just generate the remaining answers here ...

      const activeTask = store.activeTask();
      if (!activeTask || !flow.forceEndTask) return;

      try {
        patchState(store, { isLoading: true });

        await flow.forceEndTask({
          task: activeTask,
          currentIndex: store.taskItemIndex(),
          context: {
            isLastItem: store.taskItemIndex() === activeTask.items.length - 1,
            isForcedDone: store.isForcedDone(),
            itemsAmount: activeTask.items.length,
            taskItemIndex: store.taskItemIndex(),
          },
        });

        const answersDone: TaskItemResponse[] = [];
        for (let i = store.taskItemIndex() + 1; i < activeTask.items.length; i++) {
          answersDone.push({
            taskItemId: activeTask.items[i].id,
            elements: [
              {
                answer: '',
                correctAnswer: '',
                duration: 0,
                id: '',
                isCorrect: false,
              },
            ],
          });
        }

        if (answersDone.length) {
          patchState(store, (state) => ({
            answersDone: [...state.answersDone, ...answersDone],
          }));
        }
      } finally {
        patchState(store, { isLoading: false });
      }
    },
    async completeTaskDev() {
      const activeTask = store.activeTask();
      if (!activeTask || !flow.completeTaskDev) return;

      await flow.completeTaskDev({
        task: activeTask,
        currentIndex: store.taskItemIndex(),
        context: {
          isLastItem: store.taskItemIndex() === activeTask.items.length - 1,
          isForcedDone: store.isForcedDone(),
          itemsAmount: activeTask.items.length,
          taskItemIndex: store.taskItemIndex(),
        },
      });
    },
    reset() {
      patchState(store, initialState);
    },
  })),
  withComputed((state) => ({
    isCompleted: computed(() => {
      const currentTask = state.activeTask();
      if (currentTask) {
        const items = state.activeTask()?.items ?? [];
        return items.length === state.answersDone.length;
      }
      return false;
    }),
  }))
);
